import React, { useMemo, useCallback } from 'react';
import ImgText from './cells/ImgText/ImgText';
import Text from './cells/Text';
import StickyColumn from './cells/StickyColumn';
import Rang from './cells/Rang/Rang';
import Check from './cells/Check';
import Exist from './cells/Exist';
import Package from './cells/Package/Package';
import PartnerStats from './cells/PartnerStats';
import Login from './cells/Login/Login';
import Date from './cells/Date';
import Sums from './cells/Sums/Sums';
import Buyer from './cells/Buyer/Buyer';
import Status from './cells/Status/Status';
import Buttons from './cells/Buttons/Buttons';
import Actions from './cells/Actions/Actions';
import Image from './cells/Image/Image';
import Links from './cells/Links';
import SpecialStatus from './cells/SpecialStatus/SpecialStatus';
import Switcher from './cells/Switcher';
import Accordion from './cells/Accordion';
import Color from './cells/Color';
import Prices from './cells/Prices/Prices';
import TimePass from './cells/TimePass';
import ActiveRow from './cells/ActiveRow';
import DropDown from './cells/DropDown/DropDown';
import DegreePlus from './cells/DegreePlus';
import Notifications from './cells/Notifications';
import NewsTitle from './cells/NewsTitle/NewsTitle';
import OfficeTableDropdown from './cells/TableDropdownOffice/TableDropdown';
import ActivityDate from './cells/Activity/ActivityDate';
import Activity from './cells/Activity/Activity';
import Branch from './cells/Branch/Branch';
import Input from './cells/Input/Input';
import Counter from './cells/Counter/Counter';
import CounterProduct from './cells/Counter/CounterProduct';
import DegreePlusOffice from './cells/DegreePlusOffice/DegreePlusOffice';
import Progress from './cells/Progress/Progress';
import KeyValue from './cells/KeyValue/KeyValue';
import Radio from './cells/Radio/Radio';

const GridSwitcher = ({
    idx,
    alias,
    val,
    rowIndex,
    thead,
    tbody,
    setGridData,
    handlers = {},
    activeTr,
    setActiveTr,
    gridStyles,
    align,
    refTable,
    gridName,
    refTableChild,
    translation,
    isFixedLastColumnShow = false,
    showOnlyDots
}) => {
    const name = thead[idx]?.name;
    const type = thead[idx]?.type;
    const link = useMemo(() => tbody[rowIndex]?.links?.show, [tbody, rowIndex]);
    const getHandlers = useCallback(
        (key, value = false) => {
            const funcs = key
                ? handlers && handlers[name] && handlers[name][key]
                    ? handlers[name][key]
                    : false
                : handlers && handlers[name]
                ? handlers && handlers[name]
                : false;
            if (!funcs) return {};
            return Object.keys(funcs).reduce((accum, handlerName) => {
                accum[handlerName] = (e) => funcs[handlerName](tbody[rowIndex], setGridData, value, e);
                return accum;
            }, {});
        },
        [handlers, name, rowIndex, setGridData, tbody]
    );
    const commonProps = useMemo(() => {
        return {
            align,
            getHandlers,
            gridStyles,
            name,
            val,
            translation
        };
    }, [align, getHandlers, gridStyles, name, val, translation]);
    if (val?.customCell) {
        return val?.customCell;
    }
    switch (type) {
        case 'newsTitle':
            return <NewsTitle {...commonProps} link={link} />;
        case 'notifications':
            return <Notifications {...commonProps} />;
        case 'plus-degree':
            return <DegreePlus {...commonProps} />;
        case 'plus-degree-office':
            return <DegreePlusOffice {...commonProps} />;
        case 'image-text':
            return <ImgText {...commonProps} link={link} />;
        case 'text':
            return <Text {...commonProps} link={link} />;
        case 'sticky-column':
            return <StickyColumn {...commonProps} link={link} />;
        case 'contacts':
            if (!val || !Object.keys(val).filter((k) => val[k]).length) return '-';
            return (
                <OfficeTableDropdown
                    alias={alias}
                    activeTr={activeTr}
                    setActiveTr={setActiveTr}
                    refTable={refTable}
                    gridName={gridName}
                    val={val}
                    refTableChild={refTableChild}
                    showOnlyDots={showOnlyDots}
                />
            );

        case 'rang':
            return (
                <Rang
                    {...commonProps}
                    gridName={gridName}
                    refTable={refTable}
                    refTableChild={refTableChild}
                    idx={idx}
                    thead={thead}
                />
            );
        case 'deleted':
        case 'check':
            return <Check {...commonProps} />;
        case 'exist':
            return <Exist {...commonProps} />;
        case 'package':
            return <Package {...commonProps} />;
        case 'partner_stats':
            return <PartnerStats {...commonProps} />;
        case 'sponsor':
        case 'login': // user status if first column
        case 'user': // no user status label
            return <Login {...commonProps} link={link} />;
        case 'date':
            return <Date {...commonProps} />;
        case 'sums':
            return <Sums {...commonProps} />;
        case 'usernameFIO':
        case 'buyer':
            return <Buyer {...commonProps} />;
        case 'status':
            return <Status {...commonProps} />;
        case 'actions':
            return <Actions {...commonProps} refTable={refTable} gridName={gridName} refTableChild={refTableChild} />;
        case 'button':
        case 'links':
            return (
                <Buttons
                    {...commonProps}
                    refTable={refTable}
                    gridName={gridName}
                    refTableChild={refTableChild}
                    itemID={tbody[rowIndex].id}
                    tbodyRowIndexDel={tbody[rowIndex].deleted}
                    isFixedLastColumnShow={isFixedLastColumnShow}
                />
            );
        case 'image':
            return <Image {...commonProps} alt='image' />;
        case 'url':
            return <Links {...commonProps} />;
        case 'switcher':
            return <Switcher {...commonProps} />;
        case 'accordion':
            return <Accordion {...commonProps} />;
        case 'special_statuses':
            return <SpecialStatus {...commonProps} />;
        case 'color':
            return <Color {...commonProps} />;
        case 'prices':
            return <Prices {...commonProps} />;
        case 'time-passed':
            return <TimePass {...commonProps} />;
        case 'component':
            return commonProps.val;
        case 'active-row':
            return <ActiveRow {...commonProps} />;
        case 'dropdown':
            return <DropDown {...commonProps} refTable={refTable} gridName={gridName} refTableChild={refTableChild} />;
        case 'activityDate':
            return <ActivityDate {...commonProps} />;
        case 'activity':
            return <Activity {...commonProps} />;
        case 'branch':
            return <Branch {...commonProps} />;
        case 'input':
            return <Input {...commonProps} />;
        case 'counter':
            return <Counter {...commonProps} />;
        case 'counterProduct':
            return <CounterProduct {...commonProps} handlers={handlers} tbody={tbody} rowIndex={rowIndex} />;
        case 'progress':
            return <Progress {...commonProps} />;
        case 'key_value':
            return <KeyValue {...commonProps} />;
        case 'radio':
            return <Radio {...commonProps} />;
        default:
            return null;
    }
};
export default GridSwitcher;
